import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

import { ConversationSchema, TimePointSchema } from '../../requests/api/apiTypes';
import {
  Farm,
  ScatterPoint,
  Sector,
} from './types';
import { getDefaultKcColor, getRootsColor } from './utils/colorUtils';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

export const MAX_POINT_WIDTH = 15;

export const getPointWidth = (startDate: string | number | Date,
  endDate: string | number | Date) => {
  const daysDiff = dayjs(endDate).diff(dayjs(startDate), 'day');
  return Math.min(300 / Math.max(daysDiff, 1), MAX_POINT_WIDTH);
};

const NOON_MS = 12 * 60 * 60 * 1000;
const DAY_SEPARATOR_COLOR = '#999999';

export const getDayDomain = (
  startDate?: string | number | Date,
  endDate?: string | number | Date,
): { min?: number; max?: number } => ({
  min: startDate ? dayjs(startDate).utc().startOf('day').valueOf() : undefined,
  max: endDate ? dayjs(endDate).add(dayjs().utcOffset(), 'minutes').valueOf() : undefined,
});

export const anchorPointsAtNoon = <
  T extends { x: number; custom?: { date?: string | number } },
>(series: T[]): T[] => (series || []).map((p) => ({
    ...p,
    x: dayjs(p?.custom?.date ?? p.x).utc().startOf('day').valueOf() + NOON_MS,
  }));
export const getDayPlotLines = (
  min?: number,
  max?: number,
): Highcharts.XAxisPlotLinesOptions[] => {
  if (min === undefined || max === undefined) return [];
  const lines: Highcharts.XAxisPlotLinesOptions[] = [];
  let cursor = dayjs(min).utc();
  while (cursor.valueOf() <= max) {
    lines.push({
      color: DAY_SEPARATOR_COLOR,
      width: 1,
      dashStyle: 'Dash',
      value: cursor.valueOf(),
      zIndex: 3,
    });
    cursor = cursor.add(1, 'day');
  }
  return lines;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const toTimestamp = (raw: unknown): number => {
  if (typeof raw === 'number') return raw;
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw === 'string') {
    if (DATE_ONLY_PATTERN.test(raw)) return Date.parse(`${raw}T00:00:00.000Z`);
    const parsed = dayjs.utc(raw);
    return parsed.isValid() ? parsed.valueOf() : NaN;
  }
  return NaN;
};

const resolveCategoryX = (x: unknown, cats?: string[]): unknown => {
  if (typeof x !== 'number' || !Array.isArray(cats) || !cats.length) return x;
  if (!Number.isInteger(x) || x < 0 || x >= cats.length) return x;
  return cats[x];
};

/**
 * Timestamp used as the export row key. `custom.date` wins when it is a plain
 * date because daily series offset their `x` for rendering (bar widths, noon
 * anchors); those offsets must not leak into the exported date.
 */
const getPointTimestamp = (
  point: any,
  dailyBuckets: boolean,
  cats?: string[],
): number => {
  const customDate = point?.custom?.date;
  if (typeof customDate === 'string' && DATE_ONLY_PATTERN.test(customDate)) {
    return toTimestamp(customDate);
  }
  const rawX = Array.isArray(point) ? point[0] : point?.x;
  const timestamp = toTimestamp(resolveCategoryX(rawX, cats));
  if (!Number.isFinite(timestamp)) return NaN;
  return dailyBuckets ? Math.floor(timestamp / DAY_MS) * DAY_MS : timestamp;
};

/**
 * Value to export. Series that shift `y` for rendering (stacked offsets) or
 * emit filler points publish `custom.exportValue`; `null` means "no data".
 */
const getPointValue = (point: any): unknown => {
  if (Array.isArray(point)) return point[1];
  if (point && typeof point === 'object') {
    if (point.custom && 'exportValue' in point.custom) return point.custom.exportValue;
    return point.y;
  }
  return point;
};

export const getChartExportRowsData = (
  chart: Highcharts.Chart,
  categories: string[],
  title: string,
  startDate: string | number | Date,
  endDate: string | number | Date,
): { sheetName: string; rows: string[][] } | null => {
  if (!chart) return null;
  const userSeries = (chart?.userOptions?.series || []) as any[];
  const axisAny = chart?.userOptions?.xAxis as unknown;
  let cats: string[] | undefined;
  if (Array.isArray(axisAny)) {
    const firstAxis = axisAny[0] as { categories?: string[] } | undefined;
    cats = firstAxis?.categories;
  } else if (axisAny && typeof axisAny === 'object') {
    const axisObj = axisAny as { categories?: string[] };
    cats = axisObj?.categories;
  }
  if (!cats || !cats.length) {
    cats = categories;
  }
  const seriesForCsv = userSeries.filter(
    (s) => s?.type !== 'arearange' && s?.custom?.excludeFromExport !== true,
  );
  const seriesNames = seriesForCsv.map((s) => s?.name ?? 'Series');
  const seriesDataMaps = seriesForCsv.map((s) => {
    const map = new Map<number, number>();
    const dailyBuckets = s?.type === 'column' || s?.type === 'bar';
    const data = (s?.data || []) as any[];
    data.forEach((point) => {
      const timestamp = getPointTimestamp(point, dailyBuckets, cats);
      if (!Number.isFinite(timestamp)) return;
      const value = getPointValue(point);
      if (value === null || value === undefined || value === '') return;
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return;
      map.set(timestamp, parseFloat(numericValue.toFixed(2)));
    });
    return map;
  });
  const from = dayjs.utc(toTimestamp(startDate)).startOf('day').valueOf();
  const to = dayjs.utc(toTimestamp(endDate)).endOf('day').valueOf();
  const timestamps = Array.from(
    new Set(seriesDataMaps.flatMap((m) => Array.from(m.keys()))),
  )
    .filter((timestamp) => timestamp >= from && timestamp <= to)
    .sort((a, b) => a - b);
  const isDailyChart = timestamps.every((timestamp) => timestamp % DAY_MS === 0);
  const labelFormat = isDailyChart ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm';
  const headerRow = ['Fecha', ...seriesNames].map((c) => String(c));
  const dataRows = timestamps.map((timestamp) => {
    const label = dayjs.utc(timestamp).format(labelFormat);
    const values = seriesDataMaps.map((m) => (m.has(timestamp) ? String(m.get(timestamp)) : ''));
    return [label, ...values];
  });
  const rows = [headerRow, ...dataRows];
  const sheetName = (title ? title.replace(/[^a-zA-Z0-9-_\s]+/g, '') : 'chart').slice(0, 31);
  return { sheetName: sheetName || 'Datos', rows };
};

const buildMultiChartExcelContent = (sheets: { sheetName: string; rows: string[][] }[]): string => {
  const escapeXml = (s: string) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
  let xml = '<?xml version="1.0" encoding="UTF-8"?>';
  xml += '<?mso-application progid="Excel.Sheet"?>';
  xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"';
  xml += ' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">';
  sheets.forEach(({ sheetName, rows }) => {
    xml += `<Worksheet ss:Name="${escapeXml(sheetName)}">`;
    xml += '<Table>';
    rows.forEach((row) => {
      xml += '<Row>';
      row.forEach((cell) => {
        const value = String(cell);
        const type = /^-?\d+(\.\d+)?$/.test(value) ? 'Number' : 'String';
        xml += `<Cell><Data ss:Type="${type}">${escapeXml(value)}</Data></Cell>`;
      });
      xml += '</Row>';
    });
    xml += '</Table></Worksheet>';
  });
  xml += '</Workbook>';
  return xml;
};

const buildMultiChartCsvContent = (sheets: { sheetName: string; rows: string[][] }[]): string => {
  const escapeCsv = (val: string) => {
    const s = String(val ?? '');
    const needsQuotes = s.includes(',') || s.includes('"') || s.includes('\n');
    if (needsQuotes) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return sheets.map(({ sheetName, rows }) => {
    const header = `=== ${sheetName} ===`;
    const lines = rows.map((row) => row.map(escapeCsv).join(','));
    return [header, ...lines].join('\n');
  }).join('\n\n');
};

export const getChartExportContent = (
  chart: Highcharts.Chart,
  categories: string[],
  title: string,
  startDate: string | number | Date,
  endDate: string | number | Date,
  format: 'csv' | 'excel' = 'csv',
): { content: string; filename: string } | null => {
  const rowsData = getChartExportRowsData(chart, categories, title, startDate, endDate);
  if (!rowsData) return null;
  const safeTitle = (title ? title.replace(/[^a-zA-Z0-9-_]+/g, '_') : 'chart');
  if (format === 'excel') {
    const content = buildMultiChartExcelContent([rowsData]);
    return { content, filename: `${safeTitle}.xls` };
  }
  const content = buildMultiChartCsvContent([rowsData]);
  return { content, filename: `${safeTitle}.csv` };
};

export const getMultiChartExportContent = (
  chartsData: Array<{
    chart: any;
    categories: string[];
    title: string;
    startDate: string;
    endDate: string;
  }>,
  format: 'csv' | 'excel',
  filenamePrefix = 'dashboard',
): { content: string; filename: string } | null => {
  const sheets: { sheetName: string; rows: string[][] }[] = [];
  chartsData.forEach((data) => {
    const rowsData = getChartExportRowsData(
      data.chart,
      data.categories,
      data.title,
      data.startDate,
      data.endDate,
    );
    if (rowsData) sheets.push(rowsData);
  });
  if (sheets.length === 0) return null;
  const ext = format === 'excel' ? 'xls' : 'csv';
  const filename = `${filenamePrefix}.${ext}`;
  if (format === 'excel') {
    const content = buildMultiChartExcelContent(sheets);
    return { content, filename };
  }
  const content = buildMultiChartCsvContent(sheets);
  return { content, filename };
};

const buildCsvDownloader = (
  chart: Highcharts.Chart,
  categories: string[],
  title: string,
  startDate: string | number | Date,
  endDate: string | number | Date,
  format: 'csv' | 'excel' = 'csv',
) => {
  const result = getChartExportContent(chart, categories, title, startDate, endDate, format);
  if (!result) return;
  const blob = new Blob(
    [result.content],
    { type: format === 'excel' ? 'application/vnd.ms-excel' : 'text/csv;charset=utf-8;' },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const checkExistingConversation = (
  timePoint: TimePointSchema,
  existingConversations?: Array<ConversationSchema>,
) => {
  if (!existingConversations || !timePoint) return false;

  return existingConversations.some((conversation) => {
    const existingTimePoint = conversation.timePoint;
    return (
      existingTimePoint.year === timePoint.year
      && existingTimePoint.month === timePoint.month
      && existingTimePoint.day === timePoint.day
      && (!timePoint.hour || existingTimePoint.hour === timePoint.hour)
    );
  });
};

function getGranularity(startDate: string, endDate: string): 'hourly' | 'daily' | 'weekly' {
  // return 'daily';
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  // Calculate difference in days
  const diffInDays = end.diff(start, 'day', true);

  if (diffInDays <= 1) {
    return 'hourly';
  }
  if (diffInDays > 33) {
    return 'weekly';
  }
  return 'daily';
}

function mapDates(data: any, granularity: 'hourly' | 'daily' | 'weekly', itemKey: string, dateKey = 'date') {
  return new Map(
    data.map((item: any) => {
      const key = item[dateKey];
      return [key, item[itemKey]];
    }),
  );
}

function buildCategories(
  granularity: 'hourly' | 'daily' | 'weekly',
  categories: any[],
): string[] {
  if (granularity === 'hourly') {
    return categories.map((category) => {
      if (category === '') {
        return '';
      }
      return dayjs(category).utc().format('HH:mm');
    });
  }

  if (granularity === 'daily' || granularity === 'weekly') {
    return categories.map((category) => {
      if (category === '') {
        return '';
      }
      return dayjs(category).utc().format('MMM DD');
    });
  }

  return categories;
}

const buildLinearCategories = (
  timestamps: string[],
): string[] => timestamps.map((timestamp, index, arr) => {
  const currentDate = dayjs(timestamp);

  if (index === 0 || currentDate.date() !== dayjs(arr[index - 1]).date()) {
    return currentDate.format('MMM DD');
  }

  return ''; // Return empty string for all other timestamps
});

const buildCategoriesFromDates = (
  startDate: string,
  endDate: string,
) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const uniqueDates: string[] = [];

  let current = start;

  while (current.isSameOrBefore(end)) {
    uniqueDates.push(current.startOf('day').format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }

  return uniqueDates;
};

interface WeeklyDataEntry {
  date: string;
  startWeekDay: string;
  weekEnd: string;
  weekAverage: number;
}

function getWeeklyAverageData(data: Map<string, number>): Map<string, number> {
  const weeklyData: WeeklyDataEntry[] = [];
  const entries = Array.from(data.entries());

  for (let i = 0; i < entries.length; i += 7) {
    const weekEntries = entries.slice(i, i + 7);
    if (weekEntries.length > 0) {
      const startWeekDay = weekEntries[0][0];
      const weekEnd = weekEntries[weekEntries.length - 1]?.[0] || startWeekDay;

      const sum = weekEntries.reduce((acc, [, value]) => acc + value, 0);
      const average = sum / weekEntries.length;

      weeklyData.push({
        date: startWeekDay,
        startWeekDay,
        weekEnd,
        weekAverage: Number(average.toFixed(2)),
      });
    }
  }
  const response = mapDates(weeklyData, 'weekly', 'weekAverage') as Map<string, number>;
  return response;
}

interface WeeklyAddedDataEntry {
  date: string;
  startWeekDay: string;
  weekEnd: string;
  weekTotal: number;
}

function getWeeklyAddedData(data: Map<string, number>): Map<string, number> {
  const weeklyData: WeeklyAddedDataEntry[] = [];
  const entries = Array.from(data.entries());

  for (let i = 0; i < entries.length; i += 7) {
    const weekEntries = entries.slice(i, i + 7);
    if (weekEntries.length > 0) {
      const startWeekDay = weekEntries[0][0];
      const weekEnd = weekEntries[weekEntries.length - 1]?.[0] || startWeekDay;

      const total = weekEntries.reduce((acc, [, value]) => acc + value, 0);

      weeklyData.push({
        date: startWeekDay,
        startWeekDay,
        weekEnd,
        weekTotal: Number(total.toFixed(2)),
      });
    }
  }
  const response = mapDates(weeklyData, 'weekly', 'weekTotal') as Map<string, number>;
  return response;
}

function getWeekInterval(category: string) {
  const startDate = dayjs(category);
  const endDate = startDate.add(6, 'day');
  return `${startDate.format('MMM DD')} - ${endDate.format('MMM DD')}`;
}

function getTimePoint(timestamp: string | number | Date, y?: number) {
  const date = dayjs(timestamp);

  return {
    year: date.format('YYYY'),
    month: date.format('MM'),
    day: date.format('DD'),
    hour: date.format('HH:mm:ss'),
    ...(y != null && Number.isFinite(y) ? { y } : {}),
  };
}

// Creo que acá hay que hacer el agroup en caso de que haya varios comentarios en el mismo punto
const getScatterConversations = (
  conversations: Array<ConversationSchema>,
  granularity: 'hourly' | 'daily' | 'weekly',
  absoluteCategories: Array<string>,
  yAxisRange: { min: number, max: number },
): ScatterPoint[] => {
  if (!conversations || !absoluteCategories.length) return [];

  const scatterConversations = conversations
    .map((conversation) => {
      const { timePoint } = conversation;
      let searchDate: string;

      if (granularity === 'hourly' || granularity === 'daily') {
        // Para hourly y daily necesitamos todos los campos
        if (!timePoint.hour || !timePoint.day || !timePoint.month || !timePoint.year) return null;
        searchDate = `${timePoint.year}-${timePoint.month}-${timePoint.day} ${timePoint.hour}`;
      } else { // cambiar a else if cuando se agregue el formato monthly
        // Para weekly necesitamos tres de los campos
        if (!timePoint.day || !timePoint.month || !timePoint.year) return null;
        searchDate = `${timePoint.year}-${timePoint.month}-${timePoint.day}`;
      }

      const xValue = dayjs(searchDate).valueOf();
      // Usa la Y guardada al hacer click si existe; si no, ubica en el medio del eje
      const savedY = (timePoint as any)?.y;
      const yValue = (typeof savedY === 'number' && Number.isFinite(savedY))
        ? savedY
        : yAxisRange.min + (yAxisRange.max - yAxisRange.min) * 0.5;
      return {
        x: xValue,
        y: yValue,
        custom: {
          conversationId: conversation.conversationId,
          timePointData: timePoint,
        },
      };
    })
    .filter((point) => point !== null);

  return scatterConversations as ScatterPoint[];
};

export const formatDate = (isoDate: string, includeSeconds = true) => {
  const date = new Date(isoDate);

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  if (includeSeconds) {
    return `${day}-${month}-${year} / ${hours}:${minutes}:${seconds}`;
  }
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

export const stringToColor = (string: string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = Math.abs((hash * 31) + string.charCodeAt(i));
  }

  const colors = [
    '#385576',
    '#1450A4',
    '#3498D1',
    '#1B6CB4',
    '#70BF07',
    '#C2E84D',
    '#429655',
    '#1A3B21',
    '#82480A',
    '#3A7B9E',
  ];

  return colors[hash % colors.length];
};

export const generateBreadcrumb = (
  farm: Farm | null,
  sector: Sector | null,
  graphName?: string,
  sectorId?: string,
): string => {
  let sectorName = '';
  if (!farm) return '';
  if (!sector && !sectorId) return '';

  if (sectorId) {
    sectorName = farm.sectors?.find(
      (s) => s.id === sectorId,
    )?.name || '';
  } else if (sector) {
    sectorName = farm.sectors?.find(
      (s) => s.id === sector.id,
    )?.name || sector.name;
  }

  return graphName ? `${farm.name}/${sectorName}/${graphName}` : `${farm.name}/${sectorName}`;
};

export const getPlotBands = (absoluteCategories: string[] | undefined) => {
  const bands: any[] = [];
  let currentDay = '';
  let isAlternate = false;
  let lastTimestamp = 0;

  if (absoluteCategories) {
    absoluteCategories.forEach((dateStr: string) => {
      const date = dayjs(dateStr).utc().startOf('day');
      const day = date.format('YYYY-MM-DD');
      const timestamp = date.valueOf();

      if (day !== currentDay) {
        if (lastTimestamp > 0) {
          bands.push({
            color: isAlternate ? 'rgba(0, 0, 0, 0.02)' : 'rgba(173, 216, 230, 0.3)',
            from: lastTimestamp,
            to: timestamp,
          });
          isAlternate = !isAlternate;
        }
        currentDay = day;
        lastTimestamp = timestamp;
      }
    });
  }
  return bands;
};

export const buildDaySeparatorLines = (absoluteCategories?: string[]): any[] => {
  const lines: any[] = [];
  if (!absoluteCategories) return lines;
  let prevDay = '';
  absoluteCategories.forEach((dateStr: string) => {
    const date = dayjs(dateStr).utc().startOf('day');
    const day = date.format('YYYY-MM-DD');
    if (day !== prevDay) {
      lines.push({
        color: DAY_SEPARATOR_COLOR,
        width: 1,
        dashStyle: 'Dash',
        value: date.valueOf(),
        zIndex: 3,
      });
      prevDay = day;
    }
  });
  return lines;
};

const getDegreeDayChartConfig = (
  chartData: any,
  height: string | number,
  hideXValues: boolean,
  minTimestamp?: number,
  maxTimestamp?: number,
) => {
  const firstDay = dayjs(minTimestamp).utc().startOf('day').valueOf();
  const lastDay = dayjs(maxTimestamp).add(dayjs().utcOffset(), 'minutes').valueOf();
  return (
    {
      chart: {
        height,
        type: 'column',
      },
      xAxis: {
        type: 'datetime',
        min: firstDay,
        max: lastDay,
        categories: chartData.absoluteCategories,
        labels: {
          enabled: !hideXValues,
          format: '{value:%b %d}',
          rotation: 0,
        },
        plotBands: getPlotBands(chartData.absoluteCategories),
      },
      yAxis: {
        min: 0,
        gridLineWidth: 1,
        title: {
          text: '',
        },
      },

    }
  );
};

const getRootsChartConfig = (
  chartData: any,
  height: string | number,
  hideXValues: boolean,
  minTimestamp?: number,
  maxTimestamp?: number,
  reversedYAxis?: boolean,
  maxDetectionValue?: number,
  // eslint-disable-next-line no-unused-vars
  phenologicalStatesLabels?: { [key: string]: string },
) => {
  const firstDay = dayjs(minTimestamp).utc().startOf('day').valueOf();
  const lastDay = dayjs(maxTimestamp).add(dayjs().utcOffset(), 'minutes').valueOf();
  let dayXAxis = dayjs(minTimestamp).utc();

  return (
    {
      chart: {
        height,
        type: 'column',
        marginBottom: 10,
      },
      yAxis: {
        reversed: reversedYAxis,
        min: 0,
        max: maxDetectionValue,
        gridLineWidth: 1,
        title: {
          text: '',
        },
      },
      xAxis: {
        min: firstDay || undefined,
        max: lastDay || undefined,
        categories: chartData.absoluteCategories,
        type: 'datetime',
        labels: {
          enabled: !hideXValues,
          rotation: 0,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            const day = dayjs(this.value).utc();
            if (!dayXAxis.isSame(day, 'day')) {
              dayXAxis = day;
              return day.format('MMM DD');
            }
            return '';
          },
        },
        plotBands: (() => {
          const bands: any[] = [];
          if (chartData.absoluteCategories) {
            chartData.absoluteCategories.forEach((dateStr: string, idx: number) => {
              const date = dayjs(dateStr).utc().startOf('day');
              const day = date.format('YYYY-MM-DD');
              const timestamp = date.valueOf();
              const nextTimestamp = chartData.absoluteCategories[idx + 1]
                ? dayjs(chartData.absoluteCategories[idx + 1]).utc().startOf('day').valueOf()
                : timestamp + 24 * 60 * 60 * 1000;

              const rootsData = chartData.rootsDetection?.find(
                (item: any) => item.date === day,
              );
              const irrigationData = chartData.irrigationDetection?.find(
                (item: any) => item.date === day,
              );

              const rootsDepth = rootsData?.maxDepth || 0;
              const irrigationDepth = irrigationData?.maxDepth || 0;

              const hasData = rootsDepth > 0 || irrigationDepth > 0;

              if (hasData) {
                const backgroundColor = getRootsColor(rootsDepth, irrigationDepth);

                bands.push({
                  color: idx % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'rgba(173, 216, 230, 0.3)',
                  from: timestamp,
                  to: nextTimestamp,
                  label: {
                    enabled: true,
                    useHTML: true,
                    text: `<div style="
                        background-color: ${backgroundColor};
                        width: 60pc;
                        height: 6px;
                        border-radius: 4px;
                      "></div>`,
                    y: 5,
                  },
                });
              } else {
                bands.push({
                  color: idx % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'rgba(173, 216, 230, 0.3)',
                  from: timestamp,
                  to: nextTimestamp,
                });
              }
            });
          }
          return bands;
        })(),
      },
    });
};

const getEt0IrrigationChartConfig = (
  chartData: any,
  height: string | number,
  minTimestamp?: number,
  maxTimestamp?: number,
  reversedYAxis?: boolean,
  maxDetectionValue?: number,
  marginBottom = 20,
  labelYPosition = 75,
) => {
// const daysDiff = dayjs(maxTimestamp).diff(dayjs(minTimestamp), 'day');
  const firstDay = dayjs(minTimestamp).utc().startOf('day').valueOf();
  const lastDay = dayjs(maxTimestamp).add(dayjs().utcOffset(), 'minutes').valueOf();

  return (
    {
      chart: {
        height,
        type: 'column',
        marginBottom, // Se agrega el margen inferior parametrizable
      },
      yAxis: {
        reversed: reversedYAxis,
        min: 0,
        max: maxDetectionValue,
        gridLineWidth: 1,
        title: {
          text: '',
        },
      },
      xAxis: {
        min: firstDay || undefined,
        max: lastDay || undefined,
        categories: chartData.absoluteCategories,
        type: 'datetime',
        labels: {
          allowOverlap: true,
          overflow: 'allow',
          enabled: true,
          rotation: 0,
          step: 0,
          useHTML: true,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            return '';
          },
        },
        plotBands: (() => {
          const bands: any[] = [];
          if (chartData.absoluteCategories) {
            chartData.absoluteCategories.forEach((dateStr: string, idx: number) => {
              const date = dayjs(dateStr).utc().startOf('day');
              const timestamp = date.valueOf();
              const nextTimestamp = chartData.absoluteCategories[idx + 1]
                ? dayjs(chartData.absoluteCategories[idx + 1]).utc().startOf('day').valueOf()
                : timestamp + 24 * 60 * 60 * 1000;

              bands.push({
                color: idx % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'rgba(173, 216, 230, 0.3)',
                from: timestamp,
                to: nextTimestamp,
                label: {
                  allowOverlap: true,
                  overflow: 'allow',
                  enabled: true,
                  useHTML: true,
                  rotation: 0,
                  verticalAlign: 'top',
                  y: labelYPosition,
                  style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#385576',
                    textAlign: 'center',
                    lineHeight: '1',
                  },
                },
              });
            });
          }
          return bands;
        })(),
      },
    });
};

const getIrrigationChartConfig = (
  chartData: any,
  height: string | number,
  minTimestamp?: number,
  maxTimestamp?: number,
  reversedYAxis?: boolean,
  maxDetectionValue?: number,
  isBiweeklyReportView?: boolean,
  getKcColor?: (kcValue: number, date: string) => string,
  marginBottom = 20,
  labelYPosition = 75,
  // eslint-disable-next-line no-unused-vars
  phenologicalStatesLabels?: { [key: string]: string },
) => {
  // const daysDiff = dayjs(maxTimestamp).diff(dayjs(minTimestamp), 'day');
  const firstDay = dayjs(minTimestamp).utc().startOf('day').valueOf();
  const lastDay = dayjs(maxTimestamp).add(dayjs().utcOffset(), 'minutes').valueOf();
  const getKcColorDefault = (kcValue: number): string => getDefaultKcColor(kcValue);
  const colorFunction = getKcColor || getKcColorDefault;

  return (
    {
      chart: {
        height,
        type: 'column',
        marginBottom, // Se agrega el margen inferior parametrizable
      },
      yAxis: {
        reversed: reversedYAxis,
        min: 0,
        max: maxDetectionValue,
        gridLineWidth: 1,
        title: {
          text: '',
        },
      },
      xAxis: {
        min: firstDay || undefined,
        max: lastDay || undefined,
        categories: chartData.absoluteCategories,
        type: 'datetime',
        labels: {
          allowOverlap: true,
          overflow: 'allow',
          enabled: true,
          rotation: 0,
          step: 0,
          useHTML: true,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            return '';
          },
        },
        plotBands: (() => {
          const bands: any[] = [];
          if (chartData.absoluteCategories) {
            chartData.absoluteCategories.forEach((dateStr: string, idx: number) => {
              const date = dayjs(dateStr).utc().startOf('day');
              const day = date.format('YYYY-MM-DD');
              const timestamp = date.valueOf();
              const nextTimestamp = chartData.absoluteCategories[idx + 1]
                ? dayjs(chartData.absoluteCategories[idx + 1]).utc().startOf('day').valueOf()
                : timestamp + 24 * 60 * 60 * 1000;

              const kcValue = chartData.kcRecord[day];
              const backgroundColor = colorFunction(Number(kcValue), day);

              bands.push({
                color: idx % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'rgba(173, 216, 230, 0.3)',
                from: timestamp,
                to: nextTimestamp,
                label: {
                  allowOverlap: true,
                  overflow: 'allow',
                  enabled: true,
                  useHTML: true,
                  text: kcValue !== undefined && !Number.isNaN(Number(kcValue))
                    ? `<span style="
                        background-color: ${backgroundColor};
                        color: white;
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-weight: bold;
                        font-size: 12px;
                        position: relative;
                        z-index: 1;
                        display: inline-block;
                      ">${Number(kcValue).toFixed(2)}</span>`
                    : '',
                  rotation: 0,
                  verticalAlign: 'top',
                  y: labelYPosition,
                  style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#385576',
                    textAlign: 'center',
                    lineHeight: '1',
                  },
                },
              });
            });
          }
          return bands;
        })(),
      },
    });
};

const ONE_DAY_MS = 24 * 3600 * 1000;
const ONE_SECOND_MS = 1000;

const getPointExceptionBucket = (
  seriesXs: number[],
  x: number,
): { startDate: string; endDate: string } => {
  const xs = [...seriesXs].sort((a, b) => a - b);
  const idx = xs.indexOf(x);
  const nextX = idx > -1 && idx < xs.length - 1 ? xs[idx + 1] : undefined;
  const prevX = idx > 0 ? xs[idx - 1] : undefined;
  let gap = ONE_DAY_MS;
  if (nextX != null) gap = nextX - x;
  else if (prevX != null) gap = x - prevX;
  const half = Math.floor(gap / 2);
  return {
    startDate: dayjs.utc(x - half).format('YYYY-MM-DDTHH:mm:ss[Z]'),
    endDate: dayjs.utc(x + half - ONE_SECOND_MS).format('YYYY-MM-DDTHH:mm:ss[Z]'),
  };
};

export {
  getPointExceptionBucket,
  buildCategories, buildCategoriesFromDates, buildLinearCategories,
  checkExistingConversation, getGranularity, getScatterConversations,
  getTimePoint, getWeekInterval, getWeeklyAddedData, getWeeklyAverageData, mapDates,
  getRootsChartConfig, getIrrigationChartConfig, getDegreeDayChartConfig,
  buildCsvDownloader, getEt0IrrigationChartConfig,
};
